import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { TreoNavigationItem } from 'tsui/@treo';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { noop } from 'rxjs';

import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline } from 'app/modules/projects/projects.types';
import { Account, SavedFilter } from 'app/core/models/account.types';
import { AccountService, AuthService } from 'app/core';
import { User, UserAssociation } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { Permission } from 'app/core/enums/permission';
import { ObjectGroup } from 'app/shared/models/object-group';
import { ProjectsListService } from '../list.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

@Component({
  selector: 'projects-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectsSidebarComponent extends OnDestroyMixin implements OnInit {
  pipelines: Pipeline[];
  menuData: TreoNavigationItem[];
  _savedFilters: SavedFilter[];
  _filterId: string;
  selectedPipeline: Pipeline;
  account: Account;
  public userAssociations: UserAssociation[];
  public currentUser: User;
  viewsGroups: ObjectGroup[];

  // Private
  private _savedFiltersMetaData: TreoNavigationItem[];
  private _otherMenuData: TreoNavigationItem[];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _projectsService: ProjectsService,
    private _accountService: AccountService,
    private _userService: UserService,
    private readonly authService: AuthService,
    private readonly projectsListService: ProjectsListService,
  ) {
    super();
    this._savedFiltersMetaData = [];
    this._otherMenuData = [];
    this.menuData = [];
  }

  ngOnInit(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data) => {
      this.viewsGroups = data.viewsGroups.sort((a, b) => a.rank - b.rank);
    });

    this.projectsListService.viewsGroups$
      .pipe(
        untilComponentDestroyed(this),
        switchMap((viewsGroups) => {
          if (viewsGroups) {
            this.viewsGroups = viewsGroups.filter((vg) => vg?.id).sort((a, b) => a.rank - b.rank);
          }

          return this._projectsService.getPipelinesWithoutCache();
        }),
      )
      .subscribe(noop);

    this._projectsService.pipelines$.pipe(untilComponentDestroyed(this)).subscribe((pipelines: Pipeline[]) => {
      this.pipelines = pipelines.sort((a, b) => a.rank - b.rank);
      this._updateMenuData();
    });

    this._projectsService.changeProjectState$
      .pipe(
        untilComponentDestroyed(this),
        filter((state) => state),
        switchMap(() => {
          this._projectsService.bustPipelineCache();
          return this._projectsService.getPipelines();
        }),
      )
      .subscribe((pipelines) => {
        this.pipelines = pipelines;
        this._updateMenuData();
      });

    this._projectsService.pipeline$.pipe(untilComponentDestroyed(this)).subscribe((pipeline: Pipeline) => {
      if (pipeline) {
        this.selectedPipeline = pipeline;
        this._generateSavedFitlersLinks();
      }
    });

    this._accountService.savedFilters$.pipe(untilComponentDestroyed(this)).subscribe((savedFilters: SavedFilter[]) => {
      this._savedFilters = savedFilters;
      this._generateSavedFitlersLinks();
    });

    this._accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      if (accountData) {
        this.account = accountData;
        if (this.pipelines) {
          this._updateMenuData();
        }
      }
    });

    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => {
      if (user) {
        this.currentUser = user;
        this._updateMenuData();
      }
    });

    this._accountService.userAssociations$
      .pipe(untilComponentDestroyed(this))
      .subscribe((userAssociations: UserAssociation[]) => {
        if (userAssociations) {
          this.userAssociations = userAssociations;
          this._updateMenuData();
        }
      });

    this._activatedRoute.queryParams.pipe(untilComponentDestroyed(this)).subscribe((params) => {
      this._filterId = null;

      if (params['filterId']) {
        this._filterId = params['filterId'];
      }
      this._generateSavedFitlersLinks();
    });
    this._generateOtherMenuLinks();
    this._checkRedirectToFirstPipeline(this._activatedRoute);

    this._router.events.pipe(untilComponentDestroyed(this)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this._checkRedirectToFirstPipeline(this._activatedRoute);
      }
    });
  }

  _checkRedirectToFirstPipeline(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && (this.viewsGroups?.length || this.pipelines?.length)) {
      const groupMembers = this.viewsGroups
        ?.find((group) => group.members.length > 0)
        ?.members.map((member) => this.pipelines?.find((p) => p.id == member.groupable_id));
      let pipelineId = groupMembers?.sort((a, b) => a.rank - b.rank)[0]?.id;
      if (!pipelineId) {
        pipelineId = this.pipelines[0].id;
      }
      this._router.navigate(['./', pipelineId], { relativeTo: route });
    }
  }

  private _generateSavedFitlersLinks(): void {
    // Reset the folders menu data
    // this.setSidebarSaveFiltersItems(this._savedFilters?.filter((sf) => sf.visible) || []);
    this._savedFiltersMetaData = [];

    // Iterate through the folders
    this._savedFilters
      ?.filter((sf) => sf.visible)
      .forEach((savedFilter) => {
        // Generate menu item for the folder
        const menuItem: TreoNavigationItem = {
          id: savedFilter.id.toString(),
          title: savedFilter.name,
          type: 'basic',
          icon: 'play_circle_filled',
          disabled: this.selectedPipeline && this.selectedPipeline.pipeline_type == 'dynamic',
          classes: this._filterId == savedFilter.id.toString() ? 'selected-filter' : '',
          iconClasses: this._filterId === savedFilter.id.toString() ? 'text-primary' : '',
          function: (item) => {
            let url = this._router.url.split('?')[0];
            this._router.navigate([url], { queryParams: { filterId: item.id } });
          },
        };

        // Push the menu item to the folders menu data
        this._savedFiltersMetaData.push(menuItem);
      });

    //add clear filter
    const menuItem: TreoNavigationItem = {
      title: 'Clear Filters',
      type: 'basic',
      icon: 'remove_circle',
      function: (item) => {
        let url = this._router.url.split('?')[0];
        this._router.navigate([url], { queryParams: { filterId: 'reset' } });
      },
    };

    this._savedFiltersMetaData.push(menuItem);

    // Update the menu data
    this._updateMenuData();
  }

  canManagePipelines(): boolean {
    return this.authService.hasPermission(Permission.PIPELINE_MANAGE);
  }

  private _generateOtherMenuLinks(): void {
    if (this.canManagePipelines()) {
      this._otherMenuData.push({
        title: 'Settings',
        type: 'basic',
        icon: 'settings',
        link: '/projects/settings',
      });
    }

    this._updateMenuData();
  }

  private _updateMenuData(): void {
    // this.setSidebarItems();
    this.menuData = [];
    this.menuData = this.viewsGroups?.map((viewGroup) => {
      const filteredPipelines = this.pipelines.filter((p) => {
        return viewGroup.members.find((member) => member.groupable_id == p.id) !== undefined;
      });

      const menu = {
        title: viewGroup.label,
        type: 'collapsable',
        children: filteredPipelines.map((pipeline) => this.getPipelineMenuItem(pipeline)),
      } as TreoNavigationItem;

      return menu;
    });

    const noGroupPipelineMenuItems = this.pipelines
      .filter((p) => !p.object_group_id)
      ?.map((pipeline) => this.getPipelineMenuItem(pipeline));

    if (noGroupPipelineMenuItems?.length) this.menuData.push(...noGroupPipelineMenuItems);

    this.menuData.push({
      title: 'Saved Filters',
      type: 'group',
      children: [...this._savedFiltersMetaData],
    });

    this.menuData.push({
      type: 'spacer',
    });

    this.menuData.push(...this._otherMenuData);
  }

  private getPipelineMenuItem(pipeline: Pipeline): TreoNavigationItem {
    let menuItem: TreoNavigationItem;
    if (pipeline.pipeline_type == 'dynamic') {
      menuItem = {
        id: pipeline.id.toString(),
        title: pipeline.name,
        type: 'basic',
        icon: 'low_priority',
        link: '/projects/' + pipeline.id,
      };
    } else {
      menuItem = {
        id: pipeline.id.toString(),
        title: pipeline.name,
        type: 'basic',
        icon: 'list_alt',
        link: '/projects/' + pipeline.id,
      };
      menuItem['badge'] = {
        title: pipeline.num_projects_user_has_access_to.toString(),
        style: 'simple',
      };
    }

    return menuItem;
  }
}
